import React from "react";
import { BASE_URL } from "../../global";
import moment from "moment";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Toolbar from "@mui/material/Toolbar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Checkbox,
  Grid,
  TextField,
  Box,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MailOffIcon from "@mui/icons-material/MarkEmailUnread";
import MailIcon from "@mui/icons-material/Mail";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axiosWithToken from "../../utils/components/axiosTokenConfig";
import ListWrap from "../commonComponent/listWrap";
import { paginationrowsPerPageOptions } from "../../constants/commonConst";
import { ImportIndependentDueModal } from "./importIndependentDueModal";

const SEARCH_OPTIONS = [
  { key: "companyName",      label: "Client" },
  { key: "serviceRequestNo", label: "Service Request Number" },
];

const columns = [
  { id: "id",               label: "Sr.No.",           minWidth: 50  },
  { id: "clientName",       label: "Client",           minWidth: 160 },
  { id: "address",          label: "Address",          minWidth: 180 },
  { id: "serviceRequestNo", label: "Service Req. No.", minWidth: 160 },
  { id: "quantity",         label: "Quantity",         minWidth: 80  },
  { id: "dueDate",          label: "Due Date",         minWidth: 120 },
  { id: "actions",          label: "Actions",          minWidth: 100, align: "center" },
];

const actionOptions = [
  {
    value: "stop-mail",
    label: "Stop due mail",
    icon: <MailOffIcon fontSize="small" />,
  },
  {
    value: "active-mail",
    label: "Active due mail",
    icon: <MailIcon fontSize="small" />,
  },
];

export default function IndependentDueList() {
  const navigate = useNavigate();
  const urlPath  = useLocation();

  const [page, setPage]               = React.useState(() => Number(sessionStorage.getItem("ListPageCount")  || 0));
  const [rowsPerPage, setRowsPerPage] = React.useState(() => Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const [data, setData]           = React.useState([]);
  const [totalRows, setTotalRows] = React.useState(0);
  const [loading, setLoading]     = React.useState(false);

  const [searchBy,  setSearchBy]  = React.useState(sessionStorage.getItem("searchBy")  || "Client");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched,  setSearched]  = React.useState(false);
  const [selected,  setSelected]  = React.useState([]);
  const [actionValue, setActionValue] = React.useState("");

  const storedSearched = sessionStorage.getItem("searched");
  React.useEffect(() => {
    if (storedSearched) setSearched(true);
  }, [storedSearched]);

  const buildWhereClause = (byLabel, key) => {
    if (!byLabel || !key?.trim()) return "";
    const colMap = {
      "Client":                 "c.companyName",
      "Service Request Number": "ind.serviceRequestNo",
    };
    const col = colMap[byLabel];
    return col ? `AND LOWER(${col}) LIKE LOWER('%${key.trim()}%')` : "";
  };

  const fetchData = React.useCallback(
    async (byLabel = searchBy, key = searchKey) => {
      setLoading(true);
      const whereSearch = buildWhereClause(byLabel, key);
      try {
        const listQuery = {
          query: `SELECT ind.id, c.companyName AS clientName,
                         a.address AS address,
                         ind.serviceRequestNo, ind.quantity, ind.dueDate,
                         ind.stopDueMailStatus
                  FROM independentDueList ind
                  LEFT JOIN clients c ON c.id = ind.clientId
                  LEFT JOIN addresses a ON a.id = ind.addressId
                  WHERE ind.status != -1
                  ${whereSearch}
                  ORDER BY ind.id DESC
                  LIMIT ${rowsPerPage} OFFSET ${page * rowsPerPage}`,
        };
        const countQuery = {
          query: `SELECT COUNT(*) AS total
                  FROM independentDueList ind
                  LEFT JOIN clients c ON c.id = ind.clientId
                  WHERE ind.status != -1
                  ${whereSearch}`,
        };
        const [res, countRes] = await Promise.all([
          axiosWithToken.post(BASE_URL + "dynamic", listQuery,  { headers: { requestType: "fetchData" } }),
          axiosWithToken.post(BASE_URL + "dynamic", countQuery, { headers: { requestType: "fetchData" } }),
        ]);
        setData(res.data || []);
        setTotalRows(countRes.data?.[0]?.total || 0);
      } catch (err) {
        console.error("Error fetching list:", err);
        if (err.message !== "request_aborted") toast.error("Error fetching data");
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchBy, searchKey]
  );

  React.useEffect(() => { fetchData(); }, [page, rowsPerPage]);

  React.useEffect(() => {
    if (searched === false) fetchData();
  }, [searched]);

  // clear selection on every fresh load
  React.useEffect(() => {
    setSelected([]);
    setActionValue("");
  }, [data]);

  const handleSearch = () => {
    sessionStorage.setItem("searchBy",  searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched",  "true");
    sessionStorage.setItem("currentPath", urlPath.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setPage(0);
    setSearched(true);
    fetchData(searchBy, searchKey);
  };

  const clearSearch = () => {
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    sessionStorage.removeItem("currentPath");
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    setPage(0);
    setSearchBy("Client");
    setSearchKey("");
    setSearched(false);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    sessionStorage.setItem("currentPath", urlPath.pathname);
    sessionStorage.setItem("ListPageCount", newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(+e.target.value);
    setPage(0);
    sessionStorage.setItem("ListRowsPerPage", e.target.value);
    sessionStorage.setItem("ListPageCount", 0);
  };

  const TablePaginationActions = (props) => {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;
    return (
      <Box sx={{ flexShrink: 0, ml: 2.5 }}>
        <IconButton onClick={(e) => onPageChange(e, 0)} disabled={page === 0} aria-label="first page">
          {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
        </IconButton>
        <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
          {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
        </IconButton>
        <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
          {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
        </IconButton>
        <IconButton onClick={(e) => onPageChange(e, Math.max(0, Math.ceil(count / rowsPerPage) - 1))} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
          {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
        </IconButton>
      </Box>
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await axiosWithToken.patch(BASE_URL + `independentDueList/${id}`, { status: -1 });
      toast.success("Record deleted successfully");
      fetchData();
    } catch {
      toast.error("Error deleting record");
    }
  };

  const isSelected = (id) => selected.includes(id);

  const handleSelectRow = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    setSelected(e.target.checked ? data.map((r) => r.id) : []);
  };

  const handleStopDueMail = async () => {
    if (!window.confirm("Stop due mail for selected records?")) return;
    try {
      await axiosWithToken.post(BASE_URL + "dynamic", {
        query: `UPDATE independentDueList SET stopDueMailStatus = 2 WHERE id IN (${selected.join(",")})`,
      });
      toast.success("Due mail stopped!");
      fetchData();
    } catch {
      toast.error("Error updating due mail status");
    }
  };

  const handleActiveDueMail = async () => {
    if (!window.confirm("Activate due mail for selected records?")) return;
    try {
      await axiosWithToken.post(BASE_URL + "dynamic", {
        query: `UPDATE independentDueList SET stopDueMailStatus = 1 WHERE id IN (${selected.join(",")})`,
      });
      toast.success("Due mail activated!");
      fetchData();
    } catch {
      toast.error("Error updating due mail status");
    }
  };

  const handleContinue = () => {
    if (actionValue === "stop-mail") handleStopDueMail();
    else if (actionValue === "active-mail") handleActiveDueMail();
    setActionValue("");
  };

  const filterSection = () => (
    <Grid container spacing={1} alignItems="center" style={{ padding: "5px 0px" }}>
      <Grid item xs={6} sm={4} md={2} lg={2.5}>
        <Autocomplete
          size="small"
          sx={{ width: "100%" }}
          value={searchBy}
          options={SEARCH_OPTIONS}
          isOptionEqualToValue={(opt, val) => opt.label === val}
          renderInput={(params) => <TextField {...params} label="Search By" />}
          onInputChange={(_, newVal) => {
            setSearchBy(newVal);
            setSearched(false);
          }}
        />
      </Grid>

      <Grid item xs={6} sm={4} md={2} lg={2.5}>
        <TextField
          fullWidth
          label={"Enter " + searchBy}
          size="small"
          inputProps={{ autoComplete: "off" }}
          variant="outlined"
          value={searchKey}
          onChange={(e) => { setSearchKey(e.target.value); setSearched(false); }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />
      </Grid>

      <Grid item xs="auto">
        <Button
          variant="contained"
          size="small"
          color={searched ? "error" : "primary"}
          onClick={searched ? clearSearch : handleSearch}
        >
          {searched ? <RestartAltIcon /> : <SearchIcon />}
        </Button>
      </Grid>

      <Grid item xs="auto">
        <ImportIndependentDueModal onImportSuccess={fetchData} />
      </Grid>
    </Grid>
  );

  return (
    <ListWrap>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Toolbar className="ListCreatebutton">
          <Button variant="contained" size="small" component={Link} to="/independentDue/edit/0">
            <b>CREATE DUE</b>
          </Button>
        </Toolbar>
      </div>

      {filterSection()}

      {/* action bar — only shows when rows are selected */}
      {selected.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, margin: "10px 0px", flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={actionValue}
              onChange={(e) => setActionValue(e.target.value)}
              displayEmpty
              renderValue={(val) => {
                const opt = actionOptions.find((o) => o.value === val);
                return (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {opt ? opt.icon : <RemoveCircleOutlineIcon fontSize="small" sx={{ opacity: 0.4 }} />}
                    <span>{opt ? opt.label : "Select action"}</span>
                  </Box>
                );
              }}
            >
              {actionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  <ListItemIcon>{opt.icon}</ListItemIcon>
                  <ListItemText>{opt.label}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            size="small"
            disabled={!actionValue}
            startIcon={<PlayArrowIcon />}
            onClick={handleContinue}
          >
            Continue ({selected.length})
          </Button>
        </Box>
      )}

      <Box sx={{ width: "100%", overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, overflow: "auto" }}>
          <Table stickyHeader aria-label="independent due list" size="small" sx={{ borderCollapse: "collapse" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < data.length}
                    checked={data.length > 0 && selected.length === data.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align} style={{ minWidth: col.minWidth }}>
                    <b>{col.label}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow hover tabIndex={-1} key={row.id || index} selected={isSelected(row.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox checked={isSelected(row.id)} onChange={() => handleSelectRow(row.id)} />
                  </TableCell>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.clientName}</TableCell>
                  <TableCell>{row.address ?? "-"}</TableCell>
                  <TableCell>{row.serviceRequestNo ?? "-"}</TableCell>
                  <TableCell>{row.quantity ?? "-"}</TableCell>
                  <TableCell>{row.dueDate ? moment(row.dueDate).format("DD-MM-YYYY") : ""}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton color="primary" size="small" onClick={() => navigate(`/independentDue/edit/${row.id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton color="error" size="small" onClick={() => handleDelete(row.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {data.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    <h6><b>Data Not Found</b></h6>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={paginationrowsPerPageOptions}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          colSpan={3}
          SelectProps={{ inputProps: { "aria-label": "rows per page" }, native: true }}
          ActionsComponent={TablePaginationActions}
        />
        <ToastContainer />
      </Box>
    </ListWrap>
  );
}
